import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** Matches the destructive button in ConfirmDialog. */
const DANGER_BUTTON =
  "rounded-lg bg-danger px-4 py-2 text-dense font-semibold text-danger-on transition " +
  "hover:bg-danger-text disabled:cursor-not-allowed disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2";

/**
 * Re-confirm the account password before a sensitive 2FA action. The API has no
 * session to remember a recent confirmation, so the password travels with each
 * such request and this modal is what collects it.
 */
export default function PasswordPromptModal({
  open,
  title,
  message,
  confirmLabel,
  danger = false,
  busy,
  error,
  fieldError,
  onConfirm,
  onClose,
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (open) setPassword("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, busy]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    onConfirm(password);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-xl bg-surface shadow-xl">
        <form onSubmit={submit} noValidate>
          <div className="px-5 py-4">
            <h2 className="text-sm font-semibold">{title}</h2>
            {message && <p className="mt-2 text-sm text-ink-2">{message}</p>}

            {error && (
              <div className="mt-3 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
                {error}
              </div>
            )}

            <div className="mt-4">
              <label className="field-label" htmlFor="confirm-password">
                {t("twoFactor.currentPassword")}
              </label>
              <input
                id="confirm-password"
                type="password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
                disabled={busy}
              />
              {fieldError && <p className="field-error">{fieldError}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button type="button" onClick={onClose} disabled={busy} className="btn-secondary">
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={busy || password.length === 0}
              className={danger ? DANGER_BUTTON : "btn-primary"}
            >
              {busy ? t("twoFactor.working") : (confirmLabel ?? t("common.save"))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
